import { useEffect, useRef, useState, type RefObject } from "react";

import type { Task } from "@app/core";

import { setEdgeGestureExclusion } from "../../lib/edge-gesture.js";
import { useBlockMoveGhostStore, useSettingsStore, useTaskDialogStore, useTouchMoveStore } from "../../stores/index.js";
import type { DayCol } from "./build-days.js";
import { isCalendarBackgroundCreateSuppressed } from "./calendar-background-create-guard.js";
import type { DayPanDrag } from "./useDayPan.js";
import { inboxTouchDrop, isInboxTouchTargetActive, pointOverInbox } from "./touch-drop.js";
import {
  AUTOSCROLL_STEP_PX, AUTOSCROLL_ZONE_PX, DAY_MIN, DAYS_STEP_PX, edgePageZonePx, EDGE_PAGE_FIRST_MS, EDGE_PAGE_MS,
  findCalendarTask, findTimedTask, HORIZONTAL_PAN_SLOP_PX, LONG_PRESS_MS, MOVE_SLOP_PX, PAN_CENTER_FRAC, SNAP_MIN, suppressNextClick,
  type TouchGhost,
} from "./touch-gesture-util.js";

export type { TouchGhost } from "./touch-gesture-util.js";

interface Opts {
  enabled: boolean;
  /** Scroll viewport the listeners attach to (must be `touch-action: pan-y`). */
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Week/day all-day band; long-press starts here and timed drags can drop here. */
  allDayRef?: RefObject<HTMLDivElement | null>;
  /** The wide day track (geometry source for column/minute math). */
  trackRef: RefObject<HTMLDivElement | null>;
  days: DayCol[];
  visibleCount: number;
  hourHeight: number;
  zoomTo: (px: number) => void;
  /** Live finger-tracking pan handle (from useDayPan). */
  pan: DayPanDrag;
  onDayShift?: ((delta: number) => void) | undefined;
  onDayCountChange?: ((delta: number) => void) | undefined;
  onDrop: (day: Date, minutesFromMidnight: number, taskId: string) => void;
  onAllDayDrop?: ((day: Date, taskId: string, durationMinutes: number) => void) | undefined;
  onTapTask?: ((task: Task) => void) | undefined;
}

/**
 * Mobile touch gestures for the week/day grid:
 *  - 1-finger horizontal drag PANS the track 1:1 with the finger (live, via the
 *    pan handle) and commits a day-by-day shift on release;
 *  - 2-finger pinch: vertical span = hour-height zoom (Shift+scroll twin),
 *    horizontal span = day-count change (pinch out = fewer days);
 *  - long-press a task block/chip, then drag = move it (ghost snaps to 15 min,
 *    timed grabs preserve the grab point, all-day grabs use the finger as the
 *    timed start; finger at a left/right edge slowly pages days). The source
 *    block/chip dims (touchMove store) so it isn't doubled by the ghost.
 */
export function useTouchCalendarGestures(opts: Opts): { touchGhost: TouchGhost | null } {
  const [ghost, setGhost] = useState<TouchGhost | null>(null);
  const o = useRef(opts);
  o.current = opts;

  useEffect(() => {
    if (!opts.enabled) return;
    const elRaw = opts.scrollRef.current;
    if (!elRaw) return;
    const el: HTMLDivElement = elRaw;

    type Mode = "idle" | "pan" | "pinch" | "move" | "dead";
    let mode: Mode = "idle";
    let startX = 0, startY = 0, lastX = 0, lastY = 0, panStartX = 0;
    let lpTimer: ReturnType<typeof setTimeout> | null = null;
    let pressedBlock: { el: HTMLElement; id: string } | null = null;
    let moveId = "", moveTitle = "", moveDur = 60, grabOffsetMin = 0;
    let ghostNow: TouchGhost | null = null;
    // When a move starts with the inbox drawer open, the in-track ghost lives in
    // a nested stacking context BELOW the z-40 drawer, so it drags behind it.
    // Instead we float a top-level chip (BlockMoveGhost) above everything.
    let floatingGhost = false;
    let raf = 0;
    // Edge day-paging state: current edge direction + when we entered it / last
    // fired, for the dwell-then-repeat cadence.
    let edgeDir = 0, edgeEnterAt = 0, edgeFiredAt = 0;
    let spanX0 = 0, spanY0 = 0, hh0 = 48, daysLast = 0;
    let pinchAxis: "none" | "zoom" | "days" = "none";
    // A horizontal pan may only START in the center band (the edges are reserved
    // for the sidebar/inbox edge-swipes). Velocity samples (px/ms) drive the
    // release fling.
    let panAllowed = false;
    let velX = 0, prevX = 0, prevT = 0;
    let panRaf = 0, pendingPanDays = 0, panColW = 1, panAmpLive = 1;
    let zoomRaf = 0, pendingZoomPx = 0;
    const TAP_TARGET_X_SLOP_PX = 6;
    const TAP_TARGET_Y_SLOP_PX = 22;

    const swallowCtx = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
    const clearLp = () => { if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; } };
    const colWpx = () => el.clientWidth / Math.max(1, o.current.visibleCount);

    function schedulePanMove(days: number) {
      pendingPanDays = days;
      if (panRaf) return;
      panRaf = requestAnimationFrame(() => {
        panRaf = 0;
        if (mode === "pan") o.current.pan.moveByDays(pendingPanDays);
      });
    }

    // A pinch fires touchmove at the touch sampling rate (120 Hz on a lot of
    // Android hardware), and every distinct hour-height the gesture commits
    // re-lays-out every mounted column. Coalesce to at most one committed height
    // per frame — same shape as `schedulePanMove` above — so the gesture stays
    // finger-tracking without asking for more commits than the display can show.
    function scheduleZoom(px: number) {
      pendingZoomPx = px;
      if (zoomRaf) return;
      zoomRaf = requestAnimationFrame(() => {
        zoomRaf = 0;
        if (mode === "pinch") o.current.zoomTo(pendingZoomPx);
      });
    }

    function flushZoom() {
      if (!zoomRaf) return;
      cancelAnimationFrame(zoomRaf);
      zoomRaf = 0;
      o.current.zoomTo(pendingZoomPx);
    }

    function flushPanMove() {
      if (!panRaf) return;
      cancelAnimationFrame(panRaf);
      panRaf = 0;
      if (mode === "pan") o.current.pan.moveByDays(pendingPanDays);
    }

    // Only commit a React state change when the ghost actually moved — the rAF
    // loop ticks every frame, and re-setting an equal ghost re-rendered the grid
    // needlessly (the flicker / see-through-blocks artifact).
    function setGhostMaybe(g: TouchGhost | null) {
      if (g && ghostNow) {
        if (
          g.kind === "timed" && ghostNow.kind === "timed" &&
          g.col === ghostNow.col &&
          g.topMin === ghostNow.topMin &&
          g.durationMin === ghostNow.durationMin
        ) return;
        if (
          g.kind === "all-day" && ghostNow.kind === "all-day" &&
          g.col === ghostNow.col &&
          Math.abs(g.leftPx - ghostNow.leftPx) < 0.5 &&
          Math.abs(g.topPx - ghostNow.topPx) < 0.5 &&
          Math.abs(g.widthPx - ghostNow.widthPx) < 0.5
        ) return;
      }
      ghostNow = g;
      setGhost(g);
    }

    function computeGhost(): TouchGhost | null {
      const track = o.current.trackRef.current;
      if (!track) return null;
      const rect = track.getBoundingClientRect();
      const n = Math.max(1, o.current.days.length);
      const col = Math.max(0, Math.min(n - 1, Math.floor(((lastX - rect.left) / rect.width) * n)));
      const raw = ((lastY - rect.top) / o.current.hourHeight) * 60 - grabOffsetMin;
      const topMin = Math.max(0, Math.min(DAY_MIN - SNAP_MIN, Math.round(raw / SNAP_MIN) * SNAP_MIN));
      return { kind: "timed", col, topMin, durationMin: moveDur, title: moveTitle };
    }

    function allDayGhostAt(x: number, y: number): Extract<TouchGhost, { kind: "all-day" }> | null {
      const row = o.current.allDayRef?.current;
      if (!row) return null;
      const rowRect = row.getBoundingClientRect();
      if (x < rowRect.left || x > rowRect.right || y < rowRect.top || y > rowRect.bottom) return null;
      let best: { col: number; rect: DOMRect; dist: number } | null = null;
      for (const el of row.querySelectorAll<HTMLElement>("[data-all-day-col]")) {
        const raw = el.dataset["allDayCol"];
        if (raw === undefined) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        const col = Number(raw);
        if (!Number.isFinite(col)) continue;
        if (x >= rect.left && x < rect.right && y >= rect.top && y <= rect.bottom) {
          const topPx = Math.max(2, Math.min(rowRect.height - 22, y - rowRect.top - 10));
          return { kind: "all-day", col, leftPx: rect.left - rowRect.left, topPx, widthPx: rect.width, title: moveTitle };
        }
        const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
        const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        const dist = Math.hypot(dx, dy);
        if (!best || dist < best.dist) best = { col, rect, dist };
      }
      if (!best) return null;
      const topPx = Math.max(2, Math.min(rowRect.height - 22, y - rowRect.top - 10));
      return {
        kind: "all-day",
        col: best.col,
        leftPx: best.rect.left - rowRect.left,
        topPx,
        widthPx: best.rect.width,
        title: moveTitle,
      };
    }

    function allDayColAt(x: number, y: number): number | null {
      return allDayGhostAt(x, y)?.col ?? null;
    }

    function distanceToRect(x: number, y: number, rect: DOMRect): number {
      const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
      const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
      return Math.hypot(dx, dy);
    }

    function nearestTappedTask(x: number, y: number): Task | null {
      const track = o.current.trackRef.current;
      if (!track) return null;
      let best: { task: Task; dist: number; area: number } | null = null;
      for (const block of track.querySelectorAll<HTMLElement>("[data-task-block][data-task-id]")) {
        const id = block.dataset["taskId"];
        if (!id) continue;
        const col = block.closest<HTMLElement>("[data-col-date]");
        const colRect = col?.getBoundingClientRect();
        if (!colRect || x < colRect.left || x >= colRect.right) continue;
        const rect = block.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
        const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        if (dx > TAP_TARGET_X_SLOP_PX || dy > TAP_TARGET_Y_SLOP_PX) continue;
        const dist = distanceToRect(x, y, rect);
        const task = findTimedTask(o.current.days, id);
        if (!task) continue;
        const area = rect.width * rect.height;
        if (!best || dist < best.dist || (dist === best.dist && area < best.area)) best = { task, dist, area };
      }
      return best?.task ?? null;
    }

    function moveLoop() {
      raf = requestAnimationFrame(() => {
        if (mode !== "move") return;
        const rect = el.getBoundingClientRect();
        if (lastY < rect.top + AUTOSCROLL_ZONE_PX) el.scrollTop -= AUTOSCROLL_STEP_PX;
        else if (lastY > rect.bottom - AUTOSCROLL_ZONE_PX) el.scrollTop += AUTOSCROLL_STEP_PX;
        // Hold the block in a column-wide strip at the grid's left/right edge to
        // page the days (so a drag can cross weeks). Column-wide (vs a fixed px
        // strip) so it triggers regardless of where the block was grabbed; a
        // dwell keeps a quick drop on the edge day working. Screen-relative, so
        // holding at the edge keeps paging as the window slides underneath.
        const now = performance.now();
        // When the inbox drawer is open and the finger is over it, the inbox is
        // the drop target (un-schedule) — suppress edge-paging there (the inbox
        // sits at the grid's right edge, which would otherwise page) and hide the
        // grid ghost as a "this drops to the inbox" cue.
        const overInbox = pointOverInbox(lastX, lastY);
        const zone = edgePageZonePx(el.clientWidth, o.current.visibleCount);
        const dir = overInbox ? 0 : lastX < rect.left + zone ? -1 : lastX > rect.right - zone ? 1 : 0;
        if (dir !== edgeDir) { edgeDir = dir; edgeEnterAt = now; edgeFiredAt = 0; }
        if (dir !== 0) {
          const due = edgeFiredAt === 0 ? now - edgeEnterAt >= EDGE_PAGE_FIRST_MS : now - edgeFiredAt >= EDGE_PAGE_MS;
          if (due) { edgeFiredAt = now; o.current.onDayShift?.(dir); }
        }
        if (floatingGhost) useBlockMoveGhostStore.getState().move(lastX, lastY, overInbox);
        else setGhostMaybe(overInbox ? null : allDayGhostAt(lastX, lastY) ?? computeGhost());
        moveLoop();
      });
    }

    function armMove() {
      lpTimer = null;
      if (!pressedBlock) return;
      const task = findCalendarTask(o.current.days, pressedBlock.id);
      if (!task) return;
      mode = "move";
      moveId = task.id;
      moveTitle = task.title;
      moveDur = task.all_day ? 60 : Math.max(task.duration_minutes, SNAP_MIN);
      const rect = pressedBlock.el.getBoundingClientRect();
      grabOffsetMin = task.all_day ? 0 : ((lastY - rect.top) / o.current.hourHeight) * 60;
      edgeDir = 0; edgeEnterAt = performance.now(); edgeFiredAt = 0;
      window.addEventListener("contextmenu", swallowCtx, true);
      // Claim the screen edges so Samsung's Edge Panel / split-window gesture
      // can't hijack the drag-to-edge paging. Released in endMove.
      setEdgeGestureExclusion(true);
      useTouchMoveStore.getState().setMoving(task.id);
      try { navigator.vibrate?.(15); } catch { /* unsupported */ }
      // If the inbox drawer is open, drive the floating top-level ghost (which
      // can paint above the drawer) instead of the in-track one.
      floatingGhost = isInboxTouchTargetActive();
      if (floatingGhost) useBlockMoveGhostStore.getState().start(moveTitle, lastX, lastY);
      else setGhostMaybe(allDayGhostAt(lastX, lastY) ?? computeGhost());
      moveLoop();
    }

    function endMove(drop: boolean) {
      window.removeEventListener("contextmenu", swallowCtx, true);
      setEdgeGestureExclusion(false);
      cancelAnimationFrame(raf);
      useTouchMoveStore.getState().setMoving(null);
      // Released over the open inbox drawer -> un-schedule (handled by the inbox
      // target); over the all-day band -> convert/move as all-day; otherwise
      // drop onto the timed grid at the ghost's day + slot.
      if (drop && !inboxTouchDrop(moveId, lastX, lastY)) {
        const allDayCol = allDayColAt(lastX, lastY);
        if (allDayCol !== null && o.current.onAllDayDrop) {
          const day = o.current.days[allDayCol];
          if (day) o.current.onAllDayDrop(new Date(day.date), moveId, moveDur);
        } else if (ghostNow?.kind === "timed") {
          const day = o.current.days[ghostNow.col];
          if (day) o.current.onDrop(new Date(day.date), ghostNow.topMin, moveId);
        }
      }
      if (floatingGhost) { useBlockMoveGhostStore.getState().end(); floatingGhost = false; }
      ghostNow = null;
      setGhost(null);
      suppressNextClick();
    }

    function onStart(e: TouchEvent) {
      if (useTaskDialogStore.getState().open || isCalendarBackgroundCreateSuppressed()) {
        clearLp();
        mode = "dead";
        pressedBlock = null;
        return;
      }
      if (e.touches.length === 2) {
        clearLp();
        if (mode === "move") endMove(false);
        mode = "pinch";
        pinchAxis = "none";
        const [a, b] = [e.touches[0]!, e.touches[1]!];
        spanX0 = Math.abs(a.clientX - b.clientX);
        spanY0 = Math.abs(a.clientY - b.clientY);
        hh0 = o.current.hourHeight;
        daysLast = 0;
        e.preventDefault();
        return;
      }
      if (e.touches.length !== 1 || mode !== "idle") return;
      // The quick-create ghost (handles + body) owns its touches entirely.
      if ((e.target as HTMLElement).closest?.("[data-quick-ghost]")) { mode = "dead"; return; }
      const t = e.touches[0]!;
      startX = lastX = t.clientX;
      startY = lastY = t.clientY;
      // A pan may only START in the center band; the outer edges open the
      // sidebar/inbox drawers (useEdgeSwipe). Reset velocity sampling.
      const margin = (window.innerWidth * (1 - PAN_CENTER_FRAC)) / 2;
      panAllowed = t.clientX > margin && t.clientX < window.innerWidth - margin;
      velX = 0; prevX = t.clientX; prevT = performance.now();
      const block = (e.target as HTMLElement).closest?.("[data-task-id],[data-touch-task-id]") as HTMLElement | null;
      const id = block?.dataset["taskId"] ?? block?.dataset["touchTaskId"];
      if (block && id) {
        pressedBlock = { el: block, id };
        clearLp();
        lpTimer = setTimeout(armMove, LONG_PRESS_MS);
      } else {
        pressedBlock = null;
      }
    }

    function onMove(e: TouchEvent) {
      const t = e.touches[0];
      if (t) { lastX = t.clientX; lastY = t.clientY; }
      if (mode === "pinch") {
        e.preventDefault();
        if (e.touches.length < 2) return;
        const [a, b] = [e.touches[0]!, e.touches[1]!];
        const spanX = Math.abs(a.clientX - b.clientX);
        const spanY = Math.abs(a.clientY - b.clientY);
        if (pinchAxis === "none") {
          if (Math.abs(spanY - spanY0) > 24) pinchAxis = "zoom";
          else if (Math.abs(spanX - spanX0) > 50) pinchAxis = "days";
        }
        if (pinchAxis === "zoom" && spanY0 > 0) scheduleZoom(hh0 * (spanY / spanY0));
        if (pinchAxis === "days") {
          const steps = Math.round((spanX - spanX0) / DAYS_STEP_PX);
          const diff = steps - daysLast;
          // Pinch out (span grows) = fewer, wider days; pinch in = more days.
          if (diff !== 0) { daysLast = steps; o.current.onDayCountChange?.(-diff); }
        }
        return;
      }
      if (mode === "move") { e.preventDefault(); return; } // ghost updates from the rAF loop
      if (mode === "dead") return;
      // Rolling velocity (px/ms), forward-positive (drag left = advance).
      const now = performance.now();
      const dt = now - prevT;
      if (dt > 0) { velX = (prevX - lastX) / dt; prevX = lastX; prevT = now; }
      const dx = lastX - startX, dy = lastY - startY;
      if (lpTimer && (Math.abs(dx) > MOVE_SLOP_PX || Math.abs(dy) > MOVE_SLOP_PX)) clearLp();
      if (mode === "idle") {
        // Only a center-band horizontal drag pans; an edge drag is left to the
        // sidebar/inbox edge-swipe (so we mark it dead, not pan).
        if (Math.abs(dx) > HORIZONTAL_PAN_SLOP_PX && Math.abs(dx) > Math.abs(dy) * 1.2) {
          if (!panAllowed) { mode = "dead"; clearLp(); return; }
          mode = "pan";
          panStartX = lastX;
          panColW = Math.max(1, colWpx());
          panAmpLive = useSettingsStore.getState().touchPanAmp;
          o.current.pan.begin();
        } else if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) { mode = "dead"; clearLp(); return; }
        else return;
      }
      if (mode === "pan") {
        e.preventDefault();
        // Drag right reveals earlier days (+days); drag left reveals later. The
        // amplification (debug-tunable) moves the track MORE than the finger so a
        // short swipe covers more ground — higher = less "sticky".
        schedulePanMove(((lastX - panStartX) / panColW) * panAmpLive);
      }
    }

    function onEnd(e: TouchEvent) {
      clearLp();
      // Commit the last sampled span even if the fingers left before its frame.
      if (mode === "pinch") { flushZoom(); if (e.touches.length === 0) mode = "idle"; return; }
      if (mode === "move") { endMove(true); mode = "idle"; return; }
      if (mode === "pan") {
        flushPanMove();
        // Distance-thresholded commit (forward = drag left), AMPLIFIED to match
        // the live track movement, plus a velocity FLING so a fast flick keeps
        // going (advances extra days) instead of stopping instantly. All four
        // knobs are debug-tunable (Settings → General "Touch pan").
        const { touchPanFlingMs: flingMs, touchPanFlipPx: flipPx, touchPanMaxStep: maxStep } = useSettingsStore.getState();
        const panAmp = panAmpLive;
        const colW = panColW;
        const dragPx = (panStartX - lastX) * panAmp;
        const full = Math.trunc(dragPx / colW);
        const rem = dragPx - full * colW;
        let commit = full + (Math.abs(rem) >= Math.min(colW * 0.5, flipPx) ? Math.sign(rem) : 0);
        // Project the release velocity forward in time → extra days. Only adds
        // when it agrees with the swipe direction (a flick, not a slow drag).
        const flingDays = Math.round((velX * panAmp * flingMs) / colW);
        if (Math.sign(flingDays) === Math.sign(commit) || commit === 0) commit += flingDays;
        commit = Math.max(-maxStep, Math.min(maxStep, commit));
        // Seed the settle/commit animation with the release velocity (converted
        // to xDays day-units/sec; forward = xDays decreasing → negative).
        o.current.pan.setVelocity(-(velX * panAmp / colW) * 1000);
        // A real shift lets useDayPan animate to the new rest from the dragged
        // position (carrying the velocity = momentum); a no-op just settles.
        if (commit !== 0) o.current.onDayShift?.(commit);
        else o.current.pan.settle();
        suppressNextClick();
      }
      if (mode === "idle" && o.current.onTapTask) {
        const dx = lastX - startX, dy = lastY - startY;
        if (Math.abs(dx) <= MOVE_SLOP_PX && Math.abs(dy) <= MOVE_SLOP_PX) {
          const task = nearestTappedTask(lastX, lastY);
          if (task) {
            e.preventDefault();
            o.current.onTapTask(task);
            suppressNextClick();
          }
        }
      }
      mode = "idle";
      pressedBlock = null;
    }
    function onCancel() {
      clearLp();
      if (mode === "pinch") flushZoom();
      if (mode === "move") endMove(false);
      else if (mode === "pan") { flushPanMove(); o.current.pan.settle(); }
      mode = "idle";
      pressedBlock = null;
    }

    const allDayEl = opts.allDayRef?.current ?? null;
    const targets = allDayEl && allDayEl !== el ? [el, allDayEl] : [el];
    for (const target of targets) {
      target.addEventListener("touchstart", onStart, { passive: false });
      target.addEventListener("touchmove", onMove, { passive: false });
      target.addEventListener("touchend", onEnd, { passive: false });
      target.addEventListener("touchcancel", onCancel, { passive: true });
    }
    return () => {
      clearLp();
      cancelAnimationFrame(raf);
      cancelAnimationFrame(panRaf);
      cancelAnimationFrame(zoomRaf);
      window.removeEventListener("contextmenu", swallowCtx, true);
      setEdgeGestureExclusion(false);
      if (floatingGhost) { useBlockMoveGhostStore.getState().end(); floatingGhost = false; }
      useTouchMoveStore.getState().setMoving(null);
      for (const target of targets) {
        target.removeEventListener("touchstart", onStart);
        target.removeEventListener("touchmove", onMove);
        target.removeEventListener("touchend", onEnd);
        target.removeEventListener("touchcancel", onCancel);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.enabled]);

  return { touchGhost: ghost };
}
